import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { getStreamUrl } from '../api';

export const BannerCarousel = ({ videos = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (videos.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % videos.length);
    }, 5000); // Auto-slide toutes les 5 secondes

    return () => clearInterval(interval);
  }, [videos.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % videos.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleVideoClick = (videoId) => {
    navigate(`/video/${videoId}`);
  };

  if (videos.length === 0) return null;

  const currentVideo = videos[currentIndex];

  return (
    <div className="relative w-full rounded-2xl overflow-hidden mb-8 group">
      {/* Carousel Container */}
      <div className="relative aspect-[21/9] sm:aspect-[18/7] lg:aspect-[21/9] bg-gradient-to-br from-primary/20 to-accent/20">
        {/* Images */}
        <div className="absolute inset-0">
          {videos.map((video, index) => (
            <div
              key={video.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={getStreamUrl(video.thumbnail_path)}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
            </div>
          ))}
        </div>

        {/* Content Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8 z-10">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 line-clamp-2">
              {currentVideo.title}
            </h2>
            <div className="flex items-center gap-3 text-white/80 text-sm mb-4">
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">
                {currentVideo.status === 'featured' ? 'À la Une' : 
                 currentVideo.status === 'trending' ? 'Tendance' : 'Nouveau'}
              </span>
              <span>{currentVideo.year}</span>
              <span>•</span>
              <span>{currentVideo.category}</span>
            </div>
            <p className="text-white/90 text-sm sm:text-base line-clamp-2 mb-4">
              {currentVideo.description}
            </p>
            <button
              onClick={() => handleVideoClick(currentVideo.id)}
              className="flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-colors"
            >
              <Play className="w-5 h-5 fill-current" />
              Regarder
            </button>
          </div>
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100 z-10"
          aria-label="Précédent"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100 z-10"
          aria-label="Suivant"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Indicators */}
        <div className="absolute bottom-4 right-4 flex gap-2 z-10">
          {videos.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Aller à la slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
